import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Divider, LinearProgress, Skeleton, Typography } from "@mui/material";

import { useGetGinoCredits } from "../../../GINO/api/queries/ginoCredits";

import { GinoAddOnsButtonIcon } from "../../assets/icons/plans-icons";
import { GinoCreditModal } from "../../../GINO/components/GinoHeader/GinoCreditModal";

export const CreditsInfo = () => {
  const [openCreditLeftModal, setOpenCreditLeftModal] = useState(false);
  const { t, i18n } = useTranslation("plans", { keyPrefix: "page.plansOverviewPage" });

  const { data: ginoCredits, isLoading: isLoadingGinoCredits, error: errorGinoCredits } = useGetGinoCredits();

  if (isLoadingGinoCredits) return <CreditInfoLoader />;
  if (errorGinoCredits || !ginoCredits) return null;

  const { balance, charged, used } = ginoCredits;
  const usedPercentage = ((balance / charged) * 100).toFixed(0);
  const usedPercentageValue = Number(usedPercentage) || 0;

  const creditLimitReached = used >= charged || balance <= 5;

  const handleOpenCreditLeftModal = () => setOpenCreditLeftModal(true);
  const handleCloseCreditLeftModal = () => setOpenCreditLeftModal(false);

  return (
    <>
      <Box sx={styles.container}>
        <Typography sx={styles.title}>{t("credits")}</Typography>
        {creditLimitReached && (
          <Box sx={styles.creditLimitReachedContainer}>
            <Typography sx={styles.creditLimitReachedTitle}>{t("creditLimitReached")}</Typography>
            <Typography sx={styles.creditLimitReachedDescription}>{t("creditLimitReachedDescription")}</Typography>
          </Box>
        )}
        <Box sx={styles.creditContainer}>
          <Box sx={styles.creditInfo}>
            <Typography sx={styles.creditTitle}>
              {t("spent")}
              <Typography component="span" sx={styles.creditValue}>
                {used}
              </Typography>
            </Typography>
            <Typography sx={styles.creditTitle}>
              {t("limit")}
              <Typography component="span" sx={styles.creditValue}>
                {charged}
              </Typography>
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={usedPercentageValue} />
        </Box>
        <Divider flexItem orientation="horizontal" />
        <Box sx={styles.buttonContainer}>
          <Button
            variant="text-md"
            onClick={handleOpenCreditLeftModal}
            endIcon={<GinoAddOnsButtonIcon />}
            sx={styles.ginoAddOnsButton}
          >
            {t("ginoAddOns")}
          </Button>
        </Box>
      </Box>
      <GinoCreditModal
        open={openCreditLeftModal}
        onClose={handleCloseCreditLeftModal}
        ginoCredits={ginoCredits}
        initialStep={1}
      />
    </>
  );
};

const CreditInfoLoader = () => {
  const { t, i18n } = useTranslation("plans", { keyPrefix: "page.plansOverviewPage" });
  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>{t("credits")}</Typography>
      <Box sx={styles.creditContainer}>
        <Box sx={styles.creditInfo}>
          <Typography component="div" sx={styles.creditTitle}>
            {t("spent")}
            <Skeleton variant="text" width={40} height={16} />
          </Typography>
          <Typography component="div" sx={styles.creditTitle}>
            {t("limit")}
            <Skeleton variant="text" width={40} height={16} />
          </Typography>
        </Box>
        <Skeleton variant="text" width="100%" height={12} />
      </Box>
      <Divider flexItem orientation="horizontal" />
      <Box sx={styles.buttonContainer}>
        <Skeleton variant="text" width={100} height={36} />
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    borderRadius: "var(--radius-xl, 12px)",
    background: "var(--Colors-Background-bg-primary, #FFF)",
    boxShadow:
      "0 20px 24px -4px var(--Colors-Effects-Shadows-shadow-xl_01, rgba(16, 24, 40, 0.08)), 0 8px 8px -4px var(--Colors-Effects-Shadows-shadow-xl_02, rgba(16, 24, 40, 0.03))",
  },

  title: {
    padding: "12px 24px 0",
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  creditContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "12px 24px",
  },
  creditTitle: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    color: "var(--Dark---600, #707070)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  creditValue: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  creditInfo: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: "12px 24px",
  },
  creditLimitReachedContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "0 24px",
  },
  creditLimitReachedTitle: {
    color: "var(--Dark---1000, #111)",
    textAlign: "center",
    fontFamily: "Montserrat",
    fontSize: "32px",
    fontStyle: "normal",
    fontWeight: 750,
    lineHeight: "normal",
  },
  creditLimitReachedDescription: {
    color: "var(--Dark---600, #707070)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
    textAlign: "center",
  },
  ginoAddOnsButton: {
    padding: 0,
    background: "transparent !important",
  },
};
